/**
 * i18n phase: food/delivery order flow.
 * Covers ChatRoomV2 OrderCardBubble + StarRatingPrompt, FoodOrdersTab (business dashboard),
 * and RiderDashboardPage. Tuples are [ko, en, vi, ru].
 */

const status = {
  placed: ['주문 확인 중', 'Confirming order', 'Đang xác nhận đơn', 'Подтверждение заказа'],
  accepted: ['조리중', 'Preparing', 'Đang chế biến', 'Готовится'],
  acceptedMart: ['포장 준비중', 'Packing order', 'Đang chuẩn bị đơn', 'Собираем заказ'],
  dispatchRequested: ['배달원 요청 중', 'Requesting rider', 'Đang tìm tài xế', 'Поиск курьера'],
  riderAssigned: ['배달원 배정됨', 'Rider assigned', 'Đã có tài xế', 'Курьер назначен'],
  pickedUp: ['픽업 완료', 'Picked up', 'Đã lấy hàng', 'Забрал заказ'],
  arrived: ['도착', 'Arrived', 'Đã đến', 'Прибыл'],
  returning: ['반송 중', 'Returning', 'Đang hoàn trả', 'Возврат заказа'],
  completed: ['완료', 'Completed', 'Hoàn tất', 'Завершено'],
  cancelled: ['취소됨', 'Cancelled', 'Đã hủy', 'Отменено'],
};

const payment = {
  cash: ['현금', 'Cash', 'Tiền mặt', 'Наличные'],
  qr: ['QR', 'QR', 'QR', 'QR'],
  transfer: ['계좌이체', 'Bank transfer', 'Chuyển khoản', 'Банковский перевод'],
  card: ['카드', 'Card', 'Thẻ', 'Карта'],
};

const deliveryMode = {
  self: ['업체 자체 배달', 'Self delivery', 'Tự giao hàng', 'Доставка своими силами'],
  rider: ['전용 배달원', 'Dedicated rider', 'Tài xế riêng', 'Свой курьер'],
  external: ['외부 배달앱 연동', 'External delivery app', 'Liên kết app giao hàng ngoài', 'Внешнее приложение доставки'],
};

const menuOptions = {
  title: ['옵션 선택', 'Choose options', 'Chọn tùy chọn', 'Выберите опции'],
  sizeTitle: ['사이즈 선택', 'Choose a size', 'Chọn kích cỡ', 'Выберите размер'],
  addToCartBtn: ['장바구니 담기', 'Add to cart', 'Thêm vào giỏ', 'В корзину'],
  closeBtn: ['닫기', 'Close', 'Đóng', 'Закрыть'],
  soldOut: ['품절된 메뉴예요', 'This item is sold out', 'Món này đã hết', 'Этот товар распродан'],
  requiredTag: ['필수', 'Required', 'Bắt buộc', 'Обязательно'],
  singleTag: ['1개 선택', 'Pick one', 'Chọn 1', 'Выбрать одно'],
  multiTag: ['중복 선택', 'Pick multiple', 'Chọn nhiều', 'Выбрать несколько'],
};

/** Baemin-style /catalog menu detail */
const menuDetail = {
  photoDisclaimer: [
    '메뉴 사진은 실제 조리된 음식과 다를 수 있어요',
    'Menu photos may differ from the actual dish',
    'Ảnh món có thể khác với món thực tế',
    'Фото может отличаться от готового блюда',
  ],
  priceSection: ['가격', 'Price', 'Giá', 'Цена'],
  addOnsSection: ['추가', 'Add-ons', 'Thêm món', 'Дополнительно'],
  optionalTag: ['선택', 'Optional', 'Tuỳ chọn', 'Необязательно'],
  maxSelect: ['최대 {n}개 선택', 'Select up to {n}', 'Chọn tối đa {n}', 'До {n} позиций'],
  qtyLabel: ['수량', 'Quantity', 'Số lượng', 'Количество'],
  qtyCount: ['{n}개', '{n}', '{n} món', '{n} шт.'],
  addWithPrice: ['{price} 담기', 'Add {price}', 'Thêm {price}', 'Добавить {price}'],
  minOrderHint: [
    '배달 최소주문금액 {amount}',
    'Min. delivery order {amount}',
    'Đơn giao tối thiểu {amount}',
    'Мин. заказ на доставку {amount}',
  ],
  popularBadge: ['인기 메뉴', 'Popular', 'Món bán chạy', 'Популярное'],
  descMore: ['더보기', 'More', 'Xem thêm', 'Ещё'],
  descLess: ['접기', 'Less', 'Thu gọn', 'Свернуть'],
  backAria: ['뒤로', 'Back', 'Quay lại', 'Назад'],
  cartAria: ['장바구니', 'Cart', 'Giỏ hàng', 'Корзина'],
  shareAria: ['공유', 'Share', 'Chia sẻ', 'Поделиться'],
  previewOnly: ['게시 후 주문 가능', 'Available after publish', 'Có thể đặt sau khi đăng', 'Доступно после публикации'],
  selectRequired: [
    '필수 옵션을 선택해 주세요',
    'Please select required options',
    'Vui lòng chọn tuỳ chọn bắt buộc',
    'Выберите обязательные опции',
  ],
  addedToast: [
    '장바구니에 담았어요 ({count}개)',
    'Added to cart ({count})',
    'Đã thêm vào giỏ ({count})',
    'Добавлено в корзину ({count})',
  ],
};

const card = {
  stepPlaced: ['접수', 'Placed', 'Đặt hàng', 'Заказ принят'],
  stepDispatch: ['배차', 'Dispatch', 'Điều xe', 'Диспетчеризация'],
  stepDelivering: ['배달중', 'Delivering', 'Đang giao', 'В пути'],
  stepDone: ['완료', 'Done', 'Hoàn tất', 'Готово'],
  etaApprox: ['· 약 {min}분', '· ~{min} min', '· Khoảng {min} phút', '· Прибл. {min} мин'],
  etaArrival: [
    '약 {min}분 후 도착 예정',
    'Arriving in about {min} min',
    'Dự kiến đến trong khoảng {min} phút',
    'Прибытие примерно через {min} мин',
  ],
  dispatchRegional: [
    '지역 배달원 전체에게 요청 중',
    'Requesting all riders in the area',
    'Đang gửi yêu cầu tới toàn bộ tài xế khu vực',
    'Запрос отправлен всем курьерам района',
  ],
  dispatchAssigned: [
    '지정 배달원에게 요청 중',
    'Requesting the assigned rider',
    'Đang gửi yêu cầu tới tài xế được chỉ định',
    'Запрос отправлен назначенному курьеру',
  ],
  arrivedMsg: ['문 앞에 도착했어요', 'Arrived at your door', 'Tài xế đã đến trước cửa', 'Курьер у двери'],
  riderLocationTitle: [
    '배달원 실시간 위치',
    "Rider's live location",
    'Vị trí thời gian thực của tài xế',
    'Местоположение курьера в реальном времени',
  ],
  riderLocationHint: [
    '실시간 위치 · 배달중에만 표시돼요',
    'Live location · shown only while out for delivery',
    'Vị trí thời gian thực · chỉ hiển thị khi đang giao hàng',
    'В реальном времени · отображается только во время доставки',
  ],
  locationWaiting: [
    '위치 정보를 기다리는 중…',
    'Waiting for location…',
    'Đang chờ vị trí…',
    'Ожидание местоположения…',
  ],
  returningMsg: [
    '고객과 연락이 닿지 않아 반송 중이에요',
    "Returning the order — couldn't reach the customer",
    'Không liên lạc được với khách nên đang hoàn trả đơn',
    'Не удалось связаться с покупателем — заказ возвращается',
  ],
  doneMsg: [
    '현금 결제 확인 완료 · 구매인증 리뷰를 남겨보세요',
    'Cash payment confirmed · Leave a verified review',
    'Đã xác nhận thanh toán tiền mặt · Hãy để lại đánh giá đã mua',
    'Оплата наличными подтверждена · Оставьте проверенный отзыв',
  ],
  cancelledMsg: ['주문이 취소됐어요', 'The order was cancelled', 'Đơn hàng đã bị hủy', 'Заказ отменён'],
  callBusiness: ['가게에 전화', 'Call the store', 'Gọi cho cửa hàng', 'Позвонить в магазин'],
  callBuyer: ['고객에게 전화', 'Call the customer', 'Gọi cho khách hàng', 'Позвонить клиенту'],
  callRider: ['배달원에게 전화', 'Call the rider', 'Gọi cho tài xế', 'Позвонить курьеру'],
  foodAmountLabel: ['음식값', 'Food total', 'Tiền món ăn', 'Стоимость еды'],
  deliveryFeeLabel: ['배달비', 'Delivery fee', 'Phí giao hàng', 'Плата за доставку'],
  creditDiscount: [
    '크레딧 할인 -{count}개 적용',
    'Credit discount -{count} applied',
    'Đã áp dụng giảm giá -{count} credit',
    'Применена скидка кредитами -{count}',
  ],
  paymentSuffix: ['결제', 'payment', 'thanh toán', 'оплата'],
  minuteUnit: ['분', 'min', 'phút', 'мин'],
  dropoffLabel: ['수령방법', 'Drop-off', 'Cách nhận', 'Получение'],
  utensilsYes: ['수저포크 O', 'Utensils included', 'Có kèm muỗng đũa', 'Приборы включены'],
  utensilsNo: ['수저포크 X', 'No utensils', 'Không kèm muỗng đũa', 'Без приборов'],
  tierSaverBadge: ['느긋배달', 'Saver delivery', 'Giao tiết kiệm', 'Эконом-доставка'],
  scheduledForLabel: ['예약', 'Scheduled', 'Đặt trước', 'Запланировано'],
  processing: ['처리 중…', 'Processing…', 'Đang xử lý…', 'Обработка…'],
  acceptBtn: ['접수하기', 'Accept order', 'Nhận đơn', 'Принять заказ'],
  rejectBtn: ['거절', 'Decline', 'Từ chối', 'Отклонить'],
  rejectPrompt: [
    '거절 사유를 입력해 주세요 (고객에게 표시돼요)',
    'Enter a reason for declining (shown to the customer)',
    'Vui lòng nhập lý do từ chối (khách hàng sẽ thấy)',
    'Укажите причину отказа (будет показана клиенту)',
  ],
  dispatchBtn: ['배달원 호출', 'Call a rider', 'Gọi tài xế', 'Вызвать курьера'],
  selfArrivedBtn: [
    '직접 배달 완료 알림',
    'Notify self-delivery complete',
    'Báo đã tự giao hàng xong',
    'Отметить самостоятельную доставку',
  ],
  expandRegionBtn: [
    '지역 배달원으로 확대',
    'Expand to area riders',
    'Mở rộng tới tài xế khu vực',
    'Расширить на курьеров района',
  ],
  cancelClaimBtn: [
    '배정 취소하고 재배차',
    'Cancel assignment and redispatch',
    'Hủy phân công và điều lại',
    'Отменить назначение и переназначить',
  ],
  arrivedNotifyBtn: ['도착 알림', 'Notify arrival', 'Báo đã đến', 'Уведомить о прибытии'],
  completeBtn: ['완료 처리', 'Mark complete', 'Xác nhận hoàn tất', 'Отметить завершённым'],
  unreachableBtn: ['고객 부재', 'Customer unreachable', 'Không liên lạc được khách', 'Клиент недоступен'],
  confirmReturnBtn: [
    '반송 확인·환불',
    'Confirm return & refund',
    'Xác nhận hoàn trả & hoàn tiền',
    'Подтвердить возврат и возврат средств',
  ],
  ratingTitle: ['배달원은 어땠나요?', 'How was your rider?', 'Tài xế giao hàng thế nào?', 'Как вам курьер?'],
  ratingCommentPlaceholder: [
    '한줄평 (선택)',
    'Add a comment (optional)',
    'Nhận xét ngắn (không bắt buộc)',
    'Комментарий (необязательно)',
  ],
  ratingSubmit: ['별점 남기기', 'Submit rating', 'Gửi đánh giá', 'Отправить оценку'],
  ratingThanks: [
    '별점을 남겨주셔서 감사해요 ⭐',
    'Thanks for rating! ⭐',
    'Cảm ơn bạn đã đánh giá! ⭐',
    'Спасибо за оценку! ⭐',
  ],
  ratingStarAria: ['{n}점', '{n} stars', '{n} sao', '{n} звёзд'],
};

const dash = {
  creditPanelTitle: [
    '밍고크레딧 · 노출 상태',
    'Mingo Credits · Visibility status',
    'Mingo Credits · Trạng thái hiển thị',
    'Mingo Credits · Статус видимости',
  ],
  creditBalanceLabel: [
    '보유 크레딧 {amount} {currency}',
    'Credit balance {amount} {currency}',
    'Số dư credit {amount} {currency}',
    'Баланс кредитов {amount} {currency}',
  ],
  exposedMsg: [
    '노출 중이에요 (고객에게 주문 가능)',
    'Visible now (customers can order)',
    'Đang hiển thị (khách có thể đặt hàng)',
    'Сейчас видно (клиенты могут заказывать)',
  ],
  notExposedMsg: [
    '크레딧이 {min}개 이상이어야 주문을 받을 수 있어요',
    'Need at least {min} credits to receive orders',
    'Cần tối thiểu {min} credit để nhận đơn',
    'Нужно минимум {min} кредитов для приёма заказов',
  ],
  commissionLabel: ['수수료 {rate}%', 'Commission {rate}%', 'Hoa hồng {rate}%', 'Комиссия {rate}%'],
  goToWalletBtn: ['충전하러 가기', 'Top up', 'Đi nạp credit', 'Пополнить'],
  chargeNotePlaceholder: [
    '입금자명 등 메모 (선택)',
    'Depositor name or note (optional)',
    'Tên người chuyển khoản, ghi chú (không bắt buộc)',
    'Имя отправителя или заметка (необязательно)',
  ],
  chargeBtn: ['충전 요청', 'Request top-up', 'Yêu cầu nạp credit', 'Запросить пополнение'],
  chargingBtn: ['요청 중…', 'Requesting…', 'Đang gửi yêu cầu…', 'Отправка запроса…'],
  chargeSuccessMsg: [
    '충전 요청을 접수했어요. 관리자 승인 후 크레딧이 반영돼요.',
    'Top-up request received. Credits apply after admin approval.',
    'Đã nhận yêu cầu nạp credit. Credit sẽ được cộng sau khi quản trị viên duyệt.',
    'Запрос на пополнение принят. Кредиты будут начислены после одобрения администратором.',
  ],
  chargeFailMsg: [
    '충전 요청에 실패했어요.',
    'Failed to submit the top-up request.',
    'Gửi yêu cầu nạp credit thất bại.',
    'Не удалось отправить запрос на пополнение.',
  ],
  deliveryModeTitle: ['배달 설정', 'Delivery settings', 'Cài đặt giao hàng', 'Настройки доставки'],
  deliveryAvailableLabel: ['배달 사용', 'Enable delivery', 'Bật giao hàng', 'Включить доставку'],
  deliveryAvailableDesc: [
    '끄면 고객에게 배달 주문이 노출되지 않아요',
    "When off, customers won't see delivery orders from you",
    'Khi tắt, khách sẽ không thấy đơn giao hàng của bạn',
    'Если выключено, клиенты не увидят доставку',
  ],
  deliveryRadiusRequiredMsg: [
    '반경을 입력해주세요',
    'Please enter a delivery radius',
    'Vui lòng nhập bán kính giao hàng',
    'Укажите радиус доставки',
  ],
  deliveryRadiusLabel: ['배달 가능 반경', 'Delivery radius', 'Bán kính giao hàng', 'Радиус доставки'],
  deliveryRadiusHint: [
    '이 반경 밖 주문은 노출되지 않아요. 매장 이용 반경과는 별개로 설정돼요.',
    "Orders outside this radius won't be shown. Set independently from your store's service radius.",
    'Đơn hàng ngoài bán kính này sẽ không hiển thị. Cài đặt riêng với bán kính phục vụ tại chỗ.',
    'Заказы за пределами этого радиуса не отображаются. Настраивается отдельно от радиуса обслуживания магазина.',
  ],
  deliveryFeeLabel: ['배달비', 'Delivery fee', 'Phí giao hàng', 'Плата за доставку'],
  deliveryFeeBasePh: ['기본요금', 'Base fee', 'Phí cơ bản', 'Базовая плата'],
  deliveryFeeBaseHint: [
    '기본 배달비 (0원이면 허브에 「배달비 무료」로 노출돼요)',
    'Base fee (0 shows as free delivery on the hub)',
    'Phí cơ bản (0 sẽ hiện miễn phí giao trên hub)',
    'Базовая плата (0 = бесплатная доставка в хабе)',
  ],
  deliveryFeePerKmPh: ['km당 추가요금', 'Fee per km', 'Phí thêm mỗi km', 'Плата за км'],
  deliveryFeePerKmHint: [
    '기본요금에 더해 1km마다 추가되는 금액',
    'Added on top of the base fee for every extra km',
    'Cộng thêm vào phí cơ bản cho mỗi km',
    'Добавляется к базовой плате за каждый км',
  ],
  deliveryFeeDistanceHint: [
    '매장 위치가 등록돼 있어 고객 배달지까지 거리로 자동 계산돼요.',
    "Your store location is on file, so the fee is auto-calculated by delivery distance.",
    'Vị trí quán đã có, phí sẽ tự tính theo khoảng cách giao hàng.',
    'Локация магазина указана — плата рассчитывается по расстоянию доставки.',
  ],
  deliveryFeeNoCoordsHint: [
    '매장 위치가 등록되지 않아 지금은 기본요금만 적용돼요. 위치·가격 설정에서 매장 위치를 등록해보세요.',
    "Your store location isn't set, so only the base fee applies for now. Add your location in Location & pricing settings.",
    'Chưa đăng ký vị trí quán nên hiện chỉ áp dụng phí cơ bản. Hãy đăng ký vị trí trong phần Vị trí & giá.',
    'Локация магазина не указана, пока применяется только базовая плата. Добавьте локацию в настройках.',
  ],
  prepMinutesLabel: ['예상 조리시간', 'Expected prep time', 'Thời gian chuẩn bị dự kiến', 'Ожидаемое время приготовления'],
  prepMinutesUnit: ['분', 'min', 'phút', 'мин'],
  prepMinutesChip: ['{n}분', '{n} min', '{n} phút', '{n} мин'],
  orderItemCount: ['총 {n}개', '{n} items', 'Tổng {n} món', 'Всего {n}'],
  prepMinutesHint: [
    '주문을 수락할 때 이 시간이 기본값으로 제안돼요.',
    'This is suggested as the default when you accept an order.',
    'Thời gian này sẽ được đề xuất mặc định khi bạn nhận đơn.',
    'Это значение предлагается по умолчанию при принятии заказа.',
  ],
  autoAcceptLabel: ['주문 자동접수', 'Auto-accept orders', 'Tự động nhận đơn', 'Автоприём заказов'],
  autoAcceptDesc: [
    '새 주문이 들어오면 예상 조리시간으로 바로 접수 처리해요.',
    'New orders are accepted instantly using your expected prep time.',
    'Đơn mới sẽ được tự động nhận với thời gian chuẩn bị dự kiến.',
    'Новые заказы принимаются автоматически с указанным временем приготовления.',
  ],
  hubListTitle: ['허브 리스트 노출', 'Hub list exposure', 'Hiển thị trên hub', 'Показ в хабе'],
  hubListHint: [
    '음식 허브 홈의 테마 줄(인기·1인배달·배달비 무료)에 노출돼요.',
    'Shown on food hub theme rows (popular, single-portion, free delivery).',
    'Hiển thị ở hàng chủ đề hub đồ ăn (phổ biến, 1 phần, miễn phí giao).',
    'Показывается в тематических рядах хаба еды.',
  ],
  singlePortionLabel: ['1인배달 가능', 'Single-portion delivery', 'Giao 1 phần', 'Доставка на одного'],
  singlePortionDesc: [
    '1인분·혼밥 주문에 적합한 매장이면 켜 주세요',
    'Turn on if you support single-serving / solo orders',
    'Bật nếu phù hợp đơn 1 phần / ăn một mình',
    'Включите, если подходит заказ на одного',
  ],
  hubRecommendedLabel: ['인기·추천 노출', 'Popular / featured', 'Phổ biến / đề xuất', 'Популярное'],
  hubRecommendedDesc: [
    '허브 「인기」 줄에 노출 신청 (관리자가 끌 수 있어요)',
    'Request listing on the Popular row (admin may turn off)',
    'Xin hiện ở hàng Phổ biến (admin có thể tắt)',
    'Заявка в ряд «Популярное» (админ может отключить)',
  ],
  freeDeliveryHint: [
    '기본 배달비를 0원으로 저장하면 「배달비 무료」 줄에 자동 노출돼요.',
    'Set base delivery fee to 0 to appear on the Free delivery row.',
    'Đặt phí giao = 0 để tự hiện ở hàng miễn phí giao.',
    'Плата 0 — автоматическое попадание в ряд бесплатной доставки.',
  ],
  externalNotePlaceholder: [
    '예: 그랩/배달대행 연락처나 안내 문구',
    'e.g. Grab/delivery partner contact or notes',
    'VD: liên hệ Grab/đối tác giao hàng hoặc ghi chú',
    'Напр.: контакты Grab/партнёра по доставке или заметка',
  ],
  saveBtn: ['저장', 'Save', 'Lưu', 'Сохранить'],
  savingBtn: ['저장 중…', 'Saving…', 'Đang lưu…', 'Сохранение…'],
  saveSuccessMsg: ['배달 방식이 저장됐어요.', 'Delivery method saved.', 'Đã lưu phương thức giao hàng.', 'Способ доставки сохранён.'],
  saveFailMsg: ['저장에 실패했어요.', 'Failed to save.', 'Lưu thất bại.', 'Не удалось сохранить.'],
  riderPanelTitle: ['전용 배달원', 'Dedicated riders', 'Tài xế riêng', 'Свои курьеры'],
  riderPanelHint: [
    '배달 방식 설정과 무관하게 언제든 등록·관리할 수 있어요',
    'Register and manage riders anytime, regardless of your delivery mode setting',
    'Bạn có thể đăng ký và quản lý tài xế bất cứ lúc nào, không phụ thuộc vào chế độ giao hàng',
    'Вы можете регистрировать и управлять курьерами в любое время, независимо от способа доставки',
  ],
  riderEmptyMsg: [
    '등록된 배달원이 없어요',
    'No riders registered yet',
    'Chưa có tài xế nào được đăng ký',
    'Курьеры пока не добавлены',
  ],
  riderPendingSuffix: ['가입 대기', 'Pending signup', 'Chờ tham gia', 'Ожидает регистрации'],
  riderUnlinkBtn: ['연결 해제', 'Unlink', 'Huỷ liên kết', 'Отвязать'],
  riderInviteMsg: [
    '초대코드 {code} — 이 코드를 배달원 본인 폰에 전달해주세요. 라이더 화면에서 코드를 입력하면 가입돼요.',
    'Invite code {code} — share this with the rider. Entering it in the rider app completes signup.',
    'Mã mời {code} — hãy gửi mã này cho tài xế. Nhập mã trong màn hình tài xế để hoàn tất đăng ký.',
    'Код приглашения {code} — передайте его курьеру. После ввода кода в приложении курьера регистрация завершится.',
  ],
  riderQrHint: [
    '라이더가 이 QR을 카메라로 찍으면 코드 입력 없이 바로 가입돼요',
    "Scanning this QR with the rider's camera signs them up without typing the code",
    'Tài xế quét mã QR này bằng camera là đăng ký ngay, không cần nhập mã',
    'Курьер сканирует этот QR камерой — регистрация без ввода кода',
  ],
  riderNamePlaceholder: ['이름', 'Name', 'Tên', 'Имя'],
  riderPhonePlaceholder: ['연락처 *', 'Phone *', 'Số điện thoại *', 'Телефон *'],
  riderAddBtn: ['새 라이더 등록', 'Add new rider', 'Thêm tài xế mới', 'Добавить курьера'],
  riderAddingBtn: ['등록 중…', 'Adding…', 'Đang thêm…', 'Добавление…'],
  riderAddValidationMsg: [
    '이름과 연락처를 모두 입력해 주세요.',
    'Please enter both name and phone number.',
    'Vui lòng nhập cả tên và số điện thoại.',
    'Укажите имя и номер телефона.',
  ],
  riderAddFailMsg: ['라이더 등록에 실패했어요.', 'Failed to add the rider.', 'Thêm tài xế thất bại.', 'Не удалось добавить курьера.'],
  riderLinkHint: [
    '다른 지점에 이미 가입된 라이더를 연결하려면 (프랜차이즈)',
    'To link a rider already signed up at another branch (franchise)',
    'Để liên kết tài xế đã đăng ký ở chi nhánh khác (nhượng quyền)',
    'Чтобы привязать курьера, уже зарегистрированного в другом филиале (франшиза)',
  ],
  riderLinkPhonePlaceholder: [
    '가입된 라이더 전화번호',
    'Registered rider phone number',
    'Số điện thoại tài xế đã đăng ký',
    'Номер телефона зарегистрированного курьера',
  ],
  riderLinkBtn: ['연결', 'Link', 'Liên kết', 'Привязать'],
  riderLinkingBtn: ['연결 중…', 'Linking…', 'Đang liên kết…', 'Привязка…'],
  riderLinkFailMsg: [
    '해당 번호로 가입된 라이더를 찾지 못했어요.',
    'No rider found with that phone number.',
    'Không tìm thấy tài xế với số điện thoại này.',
    'Курьер с таким номером не найден.',
  ],
  riderLinkSuccessMsg: [
    '라이더를 이 지점에 연결했어요.',
    'Rider linked to this branch.',
    'Đã liên kết tài xế với chi nhánh này.',
    'Курьер привязан к этому филиалу.',
  ],
  ordersTitle: ['주문 관리', 'Order management', 'Quản lý đơn hàng', 'Управление заказами'],
  ordersHint: [
    '고객이 메뉴에서 주문하면 여기와 채팅방에 함께 표시돼요. 현금 결제를 받은 뒤 완료 처리해주세요.',
    'Customer orders appear here and in the chat room. Mark complete after receiving cash payment.',
    'Đơn hàng của khách sẽ hiện ở đây và trong phòng chat. Hãy xác nhận hoàn tất sau khi nhận thanh toán tiền mặt.',
    'Заказы клиентов отображаются здесь и в чате. Отмечайте завершённым после получения оплаты наличными.',
  ],
  loadingMsg: ['불러오는 중…', 'Loading…', 'Đang tải…', 'Загрузка…'],
  emptyMsg: ['아직 주문이 없어요', 'No orders yet', 'Chưa có đơn hàng nào', 'Заказов пока нет'],
  filterActive: ['진행중', 'Active', 'Đang xử lý', 'В процессе'],
  filterAll: ['전체', 'All', 'Tất cả', 'Все'],
  filterDone: ['완료', 'Done', 'Hoàn tất', 'Завершено'],
  filterEmptyMsg: ['해당하는 주문이 없어요', 'No matching orders', 'Không có đơn phù hợp', 'Нет подходящих заказов'],
  chatBtn: ['채팅', 'Chat', 'Trò chuyện', 'Чат'],
  customerFallback: ['고객', 'Customer', 'Khách hàng', 'Клиент'],
  cancelReasonLabel: ['취소 사유: {reason}', 'Cancellation reason: {reason}', 'Lý do hủy: {reason}', 'Причина отмены: {reason}'],
  actionSuccessMsg: ['처리했어요.', 'Done.', 'Đã xử lý.', 'Готово.'],
  actionFailMsg: ['처리에 실패했어요.', 'Action failed.', 'Xử lý thất bại.', 'Не удалось выполнить.'],
  etaPrompt: [
    '예상 도착 시간(분)을 입력해주세요',
    'Enter the estimated arrival time (minutes)',
    'Vui lòng nhập thời gian dự kiến đến (phút)',
    'Укажите примерное время прибытия (в минутах)',
  ],
};

const dashAction = {
  accept: ['접수', 'Accept', 'Nhận đơn', 'Принять'],
  reject: ['거절', 'Decline', 'Từ chối', 'Отклонить'],
  arrivedNotify: ['도착 알림', 'Notify arrival', 'Báo đã đến', 'Уведомить о прибытии'],
  dispatchRequest: ['배차 요청', 'Request dispatch', 'Yêu cầu điều xe', 'Запросить курьера'],
  regionalWaiting: [
    '지역 전체에 요청 중',
    'Requesting all area riders',
    'Đang gửi yêu cầu toàn khu vực',
    'Запрос отправлен по всему району',
  ],
  assignedWaiting: [
    '지정 라이더에게 요청 중',
    'Requesting the assigned rider',
    'Đang gửi yêu cầu tới tài xế chỉ định',
    'Запрос назначенному курьеру',
  ],
  expandRegion: ['지역으로 확대', 'Expand to area', 'Mở rộng khu vực', 'Расширить на район'],
  pickupLateWarning: [
    '배정 후 {min}분째 픽업 전이에요',
    '{min} min since assignment, not picked up yet',
    'Đã {min} phút kể từ khi phân công mà chưa lấy hàng',
    'Прошло {min} мин с назначения, заказ ещё не забран',
  ],
  cancelClaim: [
    '배정 취소하고 재배차',
    'Cancel and redispatch',
    'Hủy phân công và điều lại',
    'Отменить и переназначить',
  ],
  unreachable: ['고객 부재', 'Customer unreachable', 'Không liên lạc được khách', 'Клиент недоступен'],
  complete: ['완료 처리', 'Mark complete', 'Xác nhận hoàn tất', 'Отметить завершённым'],
  confirmReturn: [
    '반송 확인·환불',
    'Confirm return & refund',
    'Xác nhận hoàn trả & hoàn tiền',
    'Подтвердить возврат и возврат средств',
  ],
  unreachableReasonDefault: [
    '고객 연락두절',
    'Customer unreachable',
    'Không liên lạc được với khách',
    'Не удалось связаться с клиентом',
  ],
};

const rider = {
  stepAssigned: ['배정', 'Assigned', 'Đã phân công', 'Назначено'],
  stepPickedUp: ['픽업', 'Picked up', 'Đã lấy hàng', 'Забрано'],
  stepArrived: ['도착', 'Arrived', 'Đã đến', 'Прибыл'],
  stepCompleted: ['완료', 'Completed', 'Hoàn tất', 'Завершено'],
  weekLabel: ['이번주 배달 {count}건', '{count} deliveries this week', '{count} đơn giao trong tuần này', '{count} доставок на этой неделе'],
  earningsLabel: ['{amount} 배달비 정산', '{amount} settled', '{amount} đã thanh toán', '{amount} рассчитано'],
  todayLabel: ['오늘 {count}건 · {amount}', 'Today {count} · {amount}', 'Hôm nay {count} đơn · {amount}', 'Сегодня {count} · {amount}'],
  callBusiness: ['가게에 전화', 'Call the store', 'Gọi cửa hàng', 'Позвонить в магазин'],
  callBuyer: ['고객에게 전화', 'Call the customer', 'Gọi khách hàng', 'Позвонить клиенту'],
  claimBtn: ['수락하기', 'Accept', 'Nhận đơn', 'Принять'],
  claimingBtn: ['처리 중…', 'Processing…', 'Đang xử lý…', 'Обработка…'],
  cashDueLabel: ['{amount} {currency} 현금 수령', 'Collect {amount} {currency} cash', 'Thu {amount} {currency} tiền mặt', 'Получить {amount} {currency} наличными'],
  pickupCompleteBtn: ['매장 픽업 완료', 'Picked up from store', 'Đã lấy hàng tại cửa hàng', 'Забрал заказ из магазина'],
  customerArrivedBtn: ['고객 도착', 'Arrived at customer', 'Đã đến chỗ khách', 'Прибыл к клиенту'],
  completeBtn: ['배달 완료', 'Delivery complete', 'Hoàn tất giao hàng', 'Доставка завершена'],
  unreachableBtn: ['고객 연락 안 됨', 'Customer unreachable', 'Không liên lạc được khách', 'Клиент недоступен'],
  refreshAria: ['새로고침', 'Refresh', 'Làm mới', 'Обновить'],
  greeting: ['{name}님', '{name}', '{name}', '{name}'],
  notRiderMsg: [
    '라이더로 가입된 계정이 아니에요.',
    "This account isn't registered as a rider.",
    'Tài khoản này chưa đăng ký làm tài xế.',
    'Этот аккаунт не зарегистрирован как курьер.',
  ],
  joinBtn: ['초대코드로 가입하기', 'Join with invite code', 'Tham gia bằng mã mời', 'Присоединиться по коду приглашения'],
  loadingMsg: ['불러오는 중…', 'Loading…', 'Đang tải…', 'Загрузка…'],
  noRequestsMsg: [
    '지금은 대기 중인 요청이 없어요',
    'No pending requests right now',
    'Hiện không có yêu cầu nào đang chờ',
    'Сейчас нет ожидающих запросов',
  ],
  claimAlreadyMsg: [
    '이미 다른 배달원이 배정됐어요',
    'Another rider already took this',
    'Một tài xế khác đã nhận đơn này',
    'Другой курьер уже принял заказ',
  ],
  claimFailMsg: ['수락에 실패했어요', 'Failed to accept', 'Nhận đơn thất bại', 'Не удалось принять заказ'],
  actionFailMsg: ['처리에 실패했어요', 'Action failed', 'Xử lý thất bại', 'Не удалось выполнить'],
};

const hub = {
  empty: ['등록된 업체가 없습니다', 'No businesses yet', 'Chưa có cửa hàng nào', 'Пока нет заведений'],
  favorite: ['찜하기', 'Save', 'Lưu', 'Сохранить'],
  unfavorite: ['찜 해제', 'Remove from saved', 'Bỏ lưu', 'Убрать из сохранённого'],
  deliveryFee: ['배달비', 'Delivery fee', 'Phí giao hàng', 'Стоимость доставки'],
  minOrder: ['최소주문', 'Min. order', 'Đơn tối thiểu', 'Мин. заказ'],
  etaUnit: ['분', 'min', 'phút', 'мин'],
  etaPrepOnly: ['조리 약', 'Prep ~', 'Chuẩn bị ~', 'Готовка ~'],
  deliverableNow: ['지금배달가능', 'Deliverable now', 'Có thể giao ngay', 'Можно доставить сейчас'],
  menuPreview: ['메뉴', 'Menu', 'Thực đơn', 'Меню'],
  promoSectionTitle: ['지금 할인 중', 'On sale now', 'Đang giảm giá', 'Скидки сейчас'],
  sortLabel: ['정렬', 'Sort', 'Sắp xếp', 'Сортировка'],
  sponsoredLabel: ['광고', 'Ad', 'Quảng cáo', 'Реклама'],
  featuredLabel: ['추천', 'Recommended', 'Đề xuất', 'Рекомендуем'],
  couponCountdownText: [
    '{offer}이 곧 사라져요',
    '{offer} is about to expire',
    '{offer} sắp hết hạn',
    '{offer} скоро истечёт',
  ],
  locationLabel: ['주소', 'Address', 'Địa chỉ', 'Адрес'],
  allCategoryLabel: ['전체', 'All', 'Tất cả', 'Все'],
  allMenuLabel: ['메뉴 전체보기', 'View all categories', 'Xem tất cả danh mục', 'Все категории'],
  categoryMoreLabel: ['카테고리 더보기', 'More categories', 'Xem thêm danh mục', 'Больше категорий'],
  expandCategories: ['펼치기', 'Expand', 'Mở rộng', 'Развернуть'],
  collapseCategories: ['접기', 'Collapse', 'Thu gọn', 'Свернуть'],
  martRowLabel: ['마트·편의점', 'Mart & convenience', 'Siêu thị & tiện lợi', 'Магазины и супермаркеты'],
  themeSectionTitle: ['다양한 서비스를 둘러보세요', 'Explore more services', 'Khám phá thêm dịch vụ', 'Изучите другие сервисы'],
  themePopular: ['인기 음식점', 'Popular restaurants', 'Quán ăn phổ biến', 'Популярные места'],
  themeSinglePortion: ['1인배달 가능', 'Single-portion delivery', 'Giao 1 phần', 'Доставка на одного'],
  themeFreeDelivery: ['배달비 무료', 'Free delivery', 'Miễn phí giao hàng', 'Бесплатная доставка'],
  themeNoMinOrder: [
    '최소주문금액 없는 한그릇',
    'Bowls with no min. order',
    'Suất không đơn tối thiểu',
    'Без мин. заказа',
  ],
  themeFastArrival: ['금방 도착하는 가게', 'Arriving soon', 'Giao nhanh', 'Быстрая доставка'],
  themePopularMenus: ['인기메뉴', 'Popular menus', 'Món phổ biến', 'Популярные блюда'],
  themeNewStores: ['새로운 업체', 'New stores', 'Cửa hàng mới', 'Новые заведения'],
  themeFrequentStores: ['자주 시키는 업체', 'Order again', 'Thường đặt', 'Часто заказываете'],
  themePopularCafe: ['인기 카페', 'Popular cafes', 'Quán cà phê phổ biến', 'Популярные кафе'],
  themeNewStoresCafe: ['새로운 카페', 'New cafes', 'Quán cà phê mới', 'Новые кафе'],
  themeFrequentStoresCafe: ['자주 가는 카페', 'Cafes you order again', 'Quán thường đặt', 'Кафе из заказов'],
  themePopularMenusCafe: ['인기 음료·디저트', 'Popular drinks & desserts', 'Đồ uống & bánh phổ biến', 'Популярные напитки'],
  themeNoMinOrderCafe: ['최소주문 없는 카페', 'Cafes with no min. order', 'Cafe không đơn tối thiểu', 'Кафе без мин. заказа'],
  themeFastArrivalCafe: ['금방 도착하는 카페', 'Fast cafe delivery', 'Cafe giao nhanh', 'Быстрые кафе'],
  themePopularMart: ['인기 마트·편의점', 'Popular marts', 'Siêu thị phổ biến', 'Популярные магазины'],
  themeNewStoresMart: ['새로운 마트', 'New marts', 'Siêu thị mới', 'Новые магазины'],
  themeFrequentStoresMart: ['자주 사는 마트', 'Marts you order again', 'Siêu thị thường mua', 'Магазины из заказов'],
  themePopularProductsMart: ['인기 상품', 'Popular products', 'Sản phẩm phổ biến', 'Популярные товары'],
  themeNoMinOrderMart: ['최소주문 없는 마트', 'Marts with no min. order', 'Siêu thị không đơn tối thiểu', 'Магазины без мин. заказа'],
  themeFastArrivalMart: ['금방 도착하는 마트', 'Fast mart delivery', 'Siêu thị giao nhanh', 'Быстрые магазины'],
  nearbyStoresTitle: ['근처 업체', 'Nearby stores', 'Cửa hàng gần đây', 'Рядом'],
  noMinOrderBadge: ['최소주문 없음', 'No min. order', 'Không đơn tối thiểu', 'Без мин. заказа'],
  freeDeliveryBadge: ['배달비 무료', 'Free delivery', 'Miễn phí giao', 'Бесплатная доставка'],
  singlePortionBadge: ['1인배달', 'Single order', '1 phần', 'На одного'],
  recommendedBadge: ['인기', 'Popular', 'Phổ biến', 'Популярное'],
  storesInCategory: ['이 카테고리 가게', 'Stores in this category', 'Cửa hàng danh mục này', 'Заведения в категории'],
  themeCardEventsLabel: ['혜택모아보기', 'All deals', 'Tất cả ưu đãi', 'Все акции'],
  themeCardMartLabel: ['장보기·마트', 'Grocery & mart', 'Đi chợ & siêu thị', 'Продукты и магазины'],
  themeCardPickupLabel: ['포장픽업', 'Pickup', 'Tự đến lấy', 'Самовывоз'],
  paidAdRailLabel: ['추천 매장', 'Featured stores', 'Cửa hàng nổi bật', 'Рекомендуемые'],
  sponsoredBadge: ['광고', 'Ad', 'QC', 'Реклама'],
  paidAdPlaceholder: ['광고 자리 {n}', 'Ad slot {n}', 'Vị trí QC {n}', 'Рекламный слот {n}'],
  majorCategory: {
    chicken: ['치킨', 'Chicken', 'Gà rán', 'Курица'],
    chinese: ['중식', 'Chinese', 'Trung Hoa', 'Китайская'],
    donkatsu_hoe: ['돈까스·회', 'Cutlet & sashimi', 'Cốt lết & sashimi', 'Котлета и сашими'],
    pizza: ['피자', 'Pizza', 'Bánh pizza', 'Пицца'],
    fastfood: ['패스트푸드', 'Fast food', 'Đồ ăn nhanh', 'Фастфуд'],
    jjim_tang: ['찜·탕', 'Stew & soup', 'Hầm & canh', 'Тушёное и супы'],
    jokbal_bossam: ['족발·보쌈', 'Jokbal & bossam', 'Chân giò & bossam', 'Джокбаль и боссам'],
    bunsik: ['분식', 'Korean snacks', 'Ăn vặt Hàn', 'Корейские закуски'],
    cafe_dessert: ['카페·디저트', 'Cafe & dessert', 'Cafe & tráng miệng', 'Кафе и десерты'],
    coffee: ['커피', 'Coffee', 'Cà phê', 'Кофе'],
    juice: ['생과일주스', 'Fresh juice', 'Nước ép trái cây', 'Свежий сок'],
    smoothie_ade: ['스무디·에이드', 'Smoothie & ade', 'Sinh tố & ade', 'Смузи и ade'],
    tea_bubble: ['티·버블티', 'Tea & bubble tea', 'Trà & trà sữa', 'Чай и баббл-ти'],
    dessert: ['디저트·케이크', 'Dessert & cake', 'Tráng miệng & bánh', 'Десерт и торт'],
    bakery: ['베이커리·빵', 'Bakery & bread', 'Tiệm bánh & bánh mì', 'Пекарня и хлеб'],
    donut: ['도넛', 'Donut', 'Bánh donut', 'Пончик'],
    ice_cream: ['아이스크림·빙수', 'Ice cream & bingsoo', 'Kem & đá bào', 'Мороженое и бинсу'],
    korean: ['한식', 'Korean', 'Hàn Quốc', 'Корейская'],
    meat: ['고기', 'Meat', 'Thịt nướng', 'Мясо'],
    western: ['양식', 'Western', 'Âu', 'Европейская'],
    asian: ['아시안', 'Asian', 'Châu Á', 'Азиатская'],
    midnight_snack: ['야식', 'Late night', 'Ăn khuya', 'Ночная еда'],
    dosirak: ['도시락', 'Lunch box', 'Cơm hộp', 'Ланч-бокс'],
    vn_pho_bun: ['쌀국수·분짜', 'Pho & Bun', 'Phở & Bún', 'Фо и бун'],
    vn_banh_mi: ['반미', 'Banh Mi', 'Bánh mì', 'Бань ми'],
    other: ['기타', 'Other', 'Khác', 'Другое'],
  },
  martCategory: {
    ramen_noodles: ['라면·면', 'Ramen & noodles', 'Mì & phở', 'Лапша'],
    rice_grain: ['쌀·잡곡', 'Rice & grains', 'Gạo & ngũ cốc', 'Рис и крупы'],
    snack: ['과자·간식', 'Snacks', 'Bánh kẹo', 'Снеки'],
    drink: ['음료', 'Drinks', 'Đồ uống', 'Напитки'],
    fresh: ['신선식품', 'Fresh food', 'Thực phẩm tươi', 'Свежие продукты'],
    dairy_frozen: ['유제품·냉동', 'Dairy & frozen', 'Sữa & đông lạnh', 'Молочное и заморозка'],
    household: ['생활용품', 'Household', 'Đồ gia dụng', 'Хозтовары'],
    personal_care: ['생활·위생', 'Personal care', 'Chăm sóc cá nhân', 'Гигиена'],
    baby: ['유아용품', 'Baby', 'Đồ trẻ em', 'Детские товары'],
    korean_import: ['한국식품', 'Korean imports', 'Hàng Hàn', 'Корейские продукты'],
    other: ['기타', 'Other', 'Khác', 'Другое'],
  },
  locationSetupCta: ['지역을 설정해주세요', 'Set your area', 'Chọn khu vực', 'Укажите район'],
  locationUnsetBadge: ['설정 안 됨', 'Not set', 'Chưa đặt', 'Не задано'],
  setDeliveryAddress: [
    '배달주소를 설정해주세요',
    'Set your delivery address',
    'Cài đặt địa chỉ giao hàng',
    'Укажите адрес доставки',
  ],
  resetFilters: ['필터 초기화', 'Reset filters', 'Đặt lại bộ lọc', 'Сбросить фильтры'],
  searchPlaceholder: ['가게, 메뉴 검색', 'Search restaurants or marts', 'Tìm nhà hàng hoặc siêu thị', 'Поиск ресторанов и магазинов'],
  eventsTitle: ['혜택 모아보기', 'Benefits', 'Ưu đãi', 'Выгоды'],
  eventsEmpty: [
    '업체가 올린 쿠폰·이벤트가 여기에 모여요. 곧 만나요!',
    'Store coupons and promos will show up here. Check back soon!',
    'Ưu đãi/coupon từ cửa hàng sẽ hiện ở đây. Quay lại sau nhé!',
    'Купоны и акции заведений появятся здесь. Загляните позже!',
  ],
  temporarilyClosed: ['임시휴업', 'Temporarily closed', 'Tạm đóng cửa', 'Временно закрыто'],
  openNow: ['영업중', 'Open', 'Đang mở', 'Открыто'],
  closedNow: ['종료', 'Closed', 'Đã đóng', 'Закрыто'],
  hoursUnknown: ['영업시간 미등록', 'Hours not set', 'Chưa có giờ mở', 'Часы не указаны'],
  subcategoryRowAria: ['세부 카테고리', 'Subcategories', 'Danh mục phụ', 'Подкатегории'],
  filterComingSoon: ['준비 중이에요', 'Coming soon', 'Sắp có', 'Скоро'],
  chipSort: ['기본순', 'Sort', 'Sắp xếp', 'Сортировка'],
  chipDeliveryMode: ['배달방식', 'Delivery type', 'Hình thức giao', 'Способ доставки'],
  chipCoupon: ['할인쿠폰', 'Coupons', 'Mã giảm', 'Купоны'],
  chipRating: ['별점', 'Rating', 'Đánh giá', 'Рейтинг'],
  chipMinOrder: ['최소주문금액', 'Min. order', 'Đơn tối thiểu', 'Мин. заказ'],
  chipMore: ['기타', 'More', 'Khác', 'Ещё'],
  sortDefault: ['기본순', 'Default', 'Mặc định', 'По умолчанию'],
  sortPriceAsc: ['가격 낮은순', 'Price ↑', 'Giá tăng', 'Цена ↑'],
  sortPriceDesc: ['가격 높은순', 'Price ↓', 'Giá giảm', 'Цена ↓'],
  sortRating: ['별점순', 'Top rated', 'Đánh giá cao', 'По рейтингу'],
  sortDistance: ['거리순', 'Nearest', 'Gần nhất', 'Ближе'],
  storesCount: ['{n}개 업체', '{n} businesses', '{n} cửa hàng', '{n} заведений'],
  deliveryModeAll: ['전체', 'All', 'Tất cả', 'Все'],
  deliveryModeDelivery: ['배달', 'Delivery', 'Giao hàng', 'Доставка'],
  deliveryModePickup: ['픽업', 'Pickup', 'Mang đi', 'Самовывоз'],
  badgePickup: ['픽업가능', 'Pickup', 'Mang đi', 'Самовывоз'],
  badgeDelivery: ['배달가능', 'Delivery', 'Giao hàng', 'Доставка'],
  badgeNew: ['신규', 'New', 'Mới', 'Новое'],
  emptySubcategory: [
    '아직 등록된 메뉴가 없어요',
    'No menus in this category yet',
    'Chưa có món trong mục này',
    'В этой категории пока нет меню',
  ],
  etaMinutes: ['약 {n}분', '≈{n} min', '≈{n} phút', '≈{n} мин'],
  distanceKm: ['{n}km', '{n}km', '{n}km', '{n} км'],
  minOrderShort: ['최소 {amount}', 'Min {amount}', 'Tối thiểu {amount}', 'Мин. {amount}'],
};

const bizIndustry = {
  hours: {
    title: ['영업시간', 'Hours', 'Giờ mở cửa', 'Часы работы'],
    openNow: ['지금 영업중', 'Open now', 'Đang mở', 'Открыто сейчас'],
    closedNow: ['지금 마감', 'Closed now', 'Đã đóng', 'Сейчас закрыто'],
    unknown: ['영업시간 미등록', 'Hours not set', 'Chưa có giờ mở', 'Часы не указаны'],
    closedDay: ['휴무', 'Closed', 'Nghỉ', 'Выходной'],
    allDay: ['24시간', '24 hours', '24 giờ', 'Круглосуточно'],
  },
  delivery: {
    title: ['배달·포장', 'Delivery & pickup', 'Giao hàng & mang đi', 'Доставка и самовывоз'],
    available: ['배달 가능', 'Delivery available', 'Có giao hàng', 'Доставка есть'],
    unavailable: ['배달·포장 정보가 없어요', 'No delivery or pickup info', 'Chưa có thông tin giao/mang đi', 'Нет данных о доставке'],
    pickup: ['포장·픽업 가능', 'Pickup available', 'Có mang đi', 'Самовывоз есть'],
    fee: ['배달비', 'Delivery fee', 'Phí giao', 'Стоимость доставки'],
    free: ['무료', 'Free', 'Miễn phí', 'Бесплатно'],
    radius: ['배달 반경', 'Delivery radius', 'Bán kính giao', 'Радиус доставки'],
    areas: ['배달 지역', 'Delivery areas', 'Khu vực giao', 'Зоны доставки'],
  },
};

const serviceTab = {
  delivery: ['음식', 'Food', 'Ẩm thực', 'Еда'],
  pickup: ['카페·베이커리', 'Cafe·Bakery', 'Cafe·Tiệm bánh', 'Кафе·Пекарня'],
  mart: ['마트·편의점', 'Mart·Convenience', 'Siêu thị·Tiện lợi', 'Магазин·Универсам'],
  gift: ['선물하기', 'Gift', 'Tặng quà', 'Подарить'],
  event: ['이벤트', 'Events', 'Sự kiện', 'События'],
  aria: ['서비스 탭', 'Service tabs', 'Tab dịch vụ', 'Вкладки услуг'],
};

const checkout = {
  addressGeocodeFail: [
    '입력하신 주소를 찾지 못했어요. 조금 더 구체적으로 입력해주세요',
    "We couldn't find that address. Please enter it more specifically",
    'Không tìm thấy địa chỉ. Vui lòng nhập cụ thể hơn',
    'Адрес не найден. Укажите подробнее',
  ],
  openMapHelp: [
    '지도에서 위치 확인',
    'Check on map',
    'Kiểm tra trên bản đồ',
    'Проверить на карте',
  ],
  locating: ['위치 확인 중…', 'Getting location…', 'Đang lấy vị trí…', 'Определение…'],
  useMyLocation: [
    '📍 내 위치로 배달비 계산하기',
    '📍 Use my location for delivery fee',
    '📍 Dùng vị trí của tôi để tính phí',
    '📍 Моё местоположение для расчёта доставки',
  ],
  geoUnsupported: [
    '이 기기에서는 위치 확인을 지원하지 않아요',
    'This device does not support location',
    'Thiết bị này không hỗ trợ vị trí',
    'Устройство не поддерживает геолокацию',
  ],
  geoDenied: [
    '위치 확인을 허용해주셔야 배달비를 계산할 수 있어요',
    'Allow location access to calculate delivery fee',
    'Cần cho phép vị trí để tính phí giao hàng',
    'Разрешите геолокацию для расчёта доставки',
  ],
  feeEstimateFail: [
    '배달비 계산에 실패했어요',
    'Could not estimate delivery fee',
    'Không tính được phí giao hàng',
    'Не удалось рассчитать доставку',
  ],
  addressDetailLabel: [
    '상세주소 (선택)',
    'Detail address (optional)',
    'Địa chỉ chi tiết (không bắt buộc)',
    'Детали адреса (необязательно)',
  ],
  addressDetailPlaceholder: [
    '예: OO아파트 12층 1201호',
    'e.g. Building A, 12th floor, Room 1201',
    'vd: Tòa A, tầng 12, phòng 1201',
    'напр. Дом А, 12 этаж, кв. 1201',
  ],
  deliveryTierTitle: ['배달 유형', 'Delivery type', 'Loại giao hàng', 'Тип доставки'],
  deliveryTierNormalLabel: ['한집배달', 'Priority', 'Giao ngay', 'Приоритет'],
  deliveryTierSaverLabel: ['느긋배달 · {fee}', 'Saver · {fee}', 'Tiết kiệm · {fee}', 'Эконом · {fee}'],
  deliveryTierNormalHint: [
    '가장 빠르게 배달해드려요',
    "We'll deliver as fast as possible",
    'Giao hàng nhanh nhất có thể',
    'Доставим как можно быстрее',
  ],
  deliveryTierSaverHint: [
    '배달비 15% 할인 · 조금 여유있게 배달돼요',
    '15% off delivery fee · may take a bit longer',
    'Giảm 15% phí giao hàng · có thể mất thêm chút thời gian',
    'Скидка 15% на доставку · возможна небольшая задержка',
  ],
  dropoffTitle: ['배달 수령 방법', 'Drop-off method', 'Cách nhận hàng', 'Способ получения'],
  dropoffOption: {
    door_ring_leave: ['벨 누르고 문 앞에', 'Ring & leave at door', 'Bấm chuông & để trước cửa', 'Позвонить и оставить у двери'],
    door_noring_leave: ['벨 누르지 않고 문 앞에', "Don't ring, leave at door", 'Không bấm chuông, để trước cửa', 'Не звонить, оставить у двери'],
    door_leave_text: ['문 앞에 놓고 문자 주세요', 'Leave at door & text me', 'Để trước cửa & nhắn tin', 'Оставить у двери и написать'],
    hand_to_me: ['직접 받을게요', 'Hand it to me', 'Giao tận tay', 'Вручить лично'],
    concierge: ['경비실에 맡겨주세요', 'Leave with concierge', 'Gửi bảo vệ', 'Оставить у консьержа'],
    locker: ['택배함/지정장소', 'Locker / designated spot', 'Tủ đồ / vị trí chỉ định', 'Ячейка / указанное место'],
    other: ['기타', 'Other', 'Khác', 'Другое'],
  },
  dropoffNotePlaceholder: [
    '예: 공동현관 비밀번호 1234#, 3번 택배함 등 (선택)',
    'e.g. gate code 1234#, locker #3 (optional)',
    'vd: mã cổng 1234#, tủ số 3 (không bắt buộc)',
    'напр. код ворот 1234#, ячейка №3 (необязательно)',
  ],
  utensilsLabel: [
    '일회용 수저·포크 받기',
    'Include disposable utensils',
    'Nhận muỗng đũa dùng một lần',
    'Включить одноразовые приборы',
  ],
  utensilsHint: [
    '환경보호를 위해 기본적으로 제공하지 않아요',
    'Not included by default to reduce waste',
    'Mặc định không kèm để bảo vệ môi trường',
    'По умолчанию не включено ради экологии',
  ],
  kitchenNoteLabel: [
    '사장님께 요청사항 (선택)',
    'Note to the restaurant (optional)',
    'Ghi chú cho quán (không bắt buộc)',
    'Пожелание заведению (необязательно)',
  ],
  kitchenNotePlaceholder: [
    '예: 덜 맵게, 견과류 빼주세요(알레르기) 등',
    'e.g. less spicy, no nuts (allergy)',
    'vd: bớt cay, không hạt (dị ứng)',
    'напр. менее остро, без орехов (аллергия)',
  ],
  scheduleToggleLabel: [
    '예약 주문으로 받을게요',
    'Schedule for later',
    'Đặt hàng theo giờ hẹn',
    'Заказать на определённое время',
  ],
  singleStorePolicy: [
    '현재 주문은 이 매장 장바구니만 결제됩니다. 다른 매장 상품은 각 매장에서 따로 주문해 주세요.',
    'This checkout applies to this store only. Items from other stores must be ordered separately.',
    'Trang thanh toán này chỉ áp dụng cho cửa hàng hiện tại. Sản phẩm từ cửa hàng khác cần đặt riêng.',
    'Оформление относится только к текущему магазину. Товары из других магазинов нужно заказывать отдельно.',
  ],
  foodSubtotal: ['음식값', 'Food', 'Tiền món', 'Еда'],
  deliveryFeeLine: ['배달비', 'Delivery', 'Phí giao', 'Доставка'],
  grandTotal: ['합계', 'Total', 'Tổng', 'Итого'],
  paymentMethodTitle: ['결제 수단', 'Payment method', 'Phương thức thanh toán', 'Способ оплаты'],
  creditDiscount: ['보유 크레딧 {n}개로 할인 받기', 'Use {n} credits for a discount', 'Giảm giá với {n} tín dụng', 'Скидка {n} кредитами'],
  feeByDistance: [
    '거리 {km}km · 배달비 {fee}로 계산됐어요',
    'Distance {km}km · delivery fee {fee}',
    'Khoảng cách {km}km · phí giao {fee}',
    'Расстояние {km} км · доставка {fee}',
  ],
  defaultFeeNoCoords: [
    '매장 위치 정보가 없어 기본 배달비가 적용돼요',
    'Store location missing — default delivery fee applied',
    'Không có vị trí quán — áp dụng phí giao mặc định',
    'Нет координат магазина — базовая плата за доставку',
  ],
  optionalPreciseFee: [
    '위치를 확인하면 거리에 맞는 정확한 배달비를 알려드려요 (선택)',
    'Share your location for a more accurate delivery fee (optional)',
    'Chia sẻ vị trí để tính phí giao chính xác hơn (không bắt buộc)',
    'Укажите местоположение для точной стоимости доставки (необязательно)',
  ],
};

function pick(map, langIndex) {
  const out = {};
  Object.entries(map).forEach(([k, v]) => {
    if (Array.isArray(v) && v.length >= 4) out[k] = v[langIndex];
    else if (v && typeof v === 'object' && !Array.isArray(v)) out[k] = pick(v, langIndex);
    else out[k] = v;
  });
  return out;
}

function buildLang(langIndex) {
  return {
    foodOrder: {
      status: pick(status, langIndex),
      payment: pick(payment, langIndex),
      deliveryMode: pick(deliveryMode, langIndex),
      card: pick(card, langIndex),
      menuOptions: pick(menuOptions, langIndex),
      menuDetail: pick(menuDetail, langIndex),
      dash: pick(dash, langIndex),
      dashAction: pick(dashAction, langIndex),
      rider: pick(rider, langIndex),
      checkout: pick(checkout, langIndex),
    },
    foodMartHub: pick(hub, langIndex),
    serviceTab: pick(serviceTab, langIndex),
    bizIndustry: pick(bizIndustry, langIndex),
  };
}

export const phaseFoodOrderFlowKo = buildLang(0);
export const phaseFoodOrderFlowEn = buildLang(1);
export const phaseFoodOrderFlowVi = buildLang(2);
export const phaseFoodOrderFlowRu = buildLang(3);
